/**
 * React Query hooks for GV100 download API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as gv100Api from '../services/gv100Api'
import type {
  GV100AvailableVersionsResponse,
  GV100DownloadResponse,
  GV100DownloadStatus,
  GV100ImportDownloadedParams,
  GV100ImportResponse,
} from '../services/gv100Api'

// ============================================================================
// Available Versions
// ============================================================================

/**
 * Hook to fetch available GV100 versions from Destatis.
 * This makes HTTP HEAD requests to check availability, so it may take a few seconds.
 */
export function useGV100AvailableVersions(enabled = true) {
  return useQuery<GV100AvailableVersionsResponse>({
    queryKey: ['admin', 'gv100', 'available-versions'],
    queryFn: gv100Api.getAvailableVersions,
    staleTime: 5 * 60 * 1000, // 5 minutes (versions don't change often)
    enabled,
  })
}

// ============================================================================
// Download
// ============================================================================

/**
 * Hook to start a GV100 download.
 */
export function useGV100StartDownload() {
  const queryClient = useQueryClient()

  return useMutation<GV100DownloadResponse, Error, number>({
    mutationFn: (year) => gv100Api.startDownload(year),
    onSuccess: () => {
      // Invalidate versions to reflect download in progress
      queryClient.invalidateQueries({ queryKey: ['admin', 'gv100', 'available-versions'] })
    },
  })
}

/**
 * Hook to poll GV100 download status.
 * Automatically polls while status is 'pending', 'queued', or 'running'.
 */
export function useGV100DownloadStatus(jobId: string | null, enabled = true) {
  return useQuery<GV100DownloadStatus>({
    queryKey: ['admin', 'gv100', 'download-status', jobId],
    queryFn: () => gv100Api.getDownloadStatus(jobId!),
    enabled: !!jobId && enabled,
    refetchInterval: (query) => {
      const data = query.state.data
      // Poll every 1 second while in progress
      if (data && ['pending', 'queued', 'running'].includes(data.status)) {
        return 1000
      }
      // Stop polling when complete or failed
      return false
    },
    staleTime: 0, // Always fetch fresh status
  })
}

// ============================================================================
// Import Downloaded
// ============================================================================

/**
 * Hook to import downloaded GV100 files.
 */
export function useGV100ImportDownloaded() {
  const queryClient = useQueryClient()

  return useMutation<GV100ImportResponse, Error, GV100ImportDownloadedParams>({
    mutationFn: (params) => gv100Api.importDownloaded(params),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['admin', 'gv100', 'available-versions'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'editions'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'entities'] })
    },
  })
}
